import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import type { EmailCheck, EmailSettings, EmailSettingsInput } from "@/lib/email-settings";
import { useKeys } from "@/lib/queries";

/**
 * core-api's `/integrations/email` (ADR-180 in platform). Admins only, as the routes are. A
 * superadmin with more than one tenant names the tenant; everybody else gets their own.
 */

/** A tenant's Email settings, and the URL SendGrid posts the replies to. */
export function useEmailSettings(tenantId?: number, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["email-settings", tenantId ?? "own"]),
    queryFn: () => api.get<EmailSettings>(`/integrations/email${qs({ tenant_id: tenantId })}`),
    enabled,
  });
}

/**
 * Save the settings. For SendGrid core-api asks it about the key first, so this can take a second or
 * two, and it makes the reply trigger when it is missing: the Triggers page is refetched for it.
 */
export function useSaveEmailSettings(tenantId?: number) {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmailSettingsInput) =>
      api.put<EmailSettings>("/integrations/email", tenantId ? { ...input, tenant_id: tenantId } : input),
    onSuccess: (saved) => {
      qc.setQueryData(keys.scoped(["email-settings", tenantId ?? "own"]), saved);
      void qc.invalidateQueries({ queryKey: keys.scoped(["triggers"]) });
    },
  });
}

/** Read the SendGrid account and the reply domain's DNS, and say what is still wrong. Changes
 *  nothing, here or at SendGrid. */
export function useEmailCheck(tenantId?: number) {
  return useMutation({
    mutationFn: () => api.post<EmailCheck>(`/integrations/email/check${qs({ tenant_id: tenantId })}`),
  });
}
